/*
 * tol-cart-guard.js — カート手前ゲートの「CTA 状態の唯一の書き込み口」。
 *
 * 出典 / 設計正本:
 *   LB 5-10（講座/WS 同時購入制限）・LB 7-33 E-3（規約・資格の同意ゲート）
 *   DECISIONS 2026-08-31 決定 1（E-3 と 5-10 を束ねて「対応する」確定）
 *   プラン正本 = ~/.claude/plans/r12-pr-2-7-33-e-3-gleaming-thunder.md
 *
 * 何をするか / しないか:
 *   - する: サーバーが出した判定ノード（[data-tol-cart-guard-state][data-primary="true"]）を読み、
 *           document 内のすべての被ガード要素へ冪等に反映する。
 *   - する（PR-2）: 層 (b)（同意ゲート）の UI も**この 1 ファイルに同居**させる。
 *   - しない: 判定そのものの再導出。/cart/add.js・/cart/change.js の応答 items に product tags が
 *           含まれないため、クライアントでは層 (a)/(c) を導出できない（サーバーが唯一の判定者）。
 *
 * ★ 2026-10: 層 (b) に「修了証・履修証明書の氏名確認」チェックを追加（buildRequirements の末尾）。
 *   タグ「オンライン講座」の商品（snippets/tol-cart-kouza-data.liquid の data-online="true"）が 1 件でもあれば 1 つだけ出す。
 *   資格チェックと同じ class なので allRequirementsChecked の対象＝チェックするまでチェックアウトに進めない。
 *   文言は同意欄（snippets/tol-cart-kouza-agreement.liquid）の data-i18n-online-group / data-i18n-online-name。
 *   オンライン講座の有無は指紋（buildFingerprint）にも含める＝入れ替わったら同意をリセットして作り直す。
 *
 * ★ なぜ層 (b) を別 asset にしないか（二系統レビューの HIGH 指摘）:
 *   分けると「guard.js は生きて consent.js だけ死ぬ」分裂故障が生まれる。その状態では
 *   consent を満たす手段が消える一方 data-has-kouza は生きるので、kouza タグ商品（実測 271 件）を
 *   含むカートが /cart でもドロワーでも**全ページで恒久ブロック**になる（LIVE は同条件で fail-open
 *   ＝失敗の向きが逆）。1 ファイルに同居させて分裂故障を構造的に消す。
 *   残る恒久ブロックは「このファイルごと死ぬ」場合だけで、それは決定 4 が明示的に選んだ fail-closed。
 *   /cart 以外では同意 UI が DOM に無いので即 no-op する。
 *
 * ★ fail-closed の分担（critical H-2 で凍結版から反転）:
 *   ブロックの一次保証は **サーバーレンダ**（tol-cart-guard-cta.liquid が初期状態から disabled を出し、
 *   実 CTA の器 fieldset に disabled / inert / hidden が載る。層 (b) は kouza 在庫時に常にブロック側）。
 *   この JS は「カート内容が変わった後の追随」と「同意完了時の解除」だけを担う best-effort レイヤー。
 *   判定ノードが読めないときは **no-op（DOM を触らない）**——サーバーが既に正しい状態を描いているため、
 *   「ノードが無いから全部ブロック」にすると snippet の描画事故が全ユーザーの購入停止になる。
 *
 * ★ 抑止は 2 モード（LIVE 逐語）:
 *   restriction（層 (a)）= 器を hidden ＋ 代替 disabled ボタンを出す（LIVE は非描画）
 *   consent（層 (b) のみ）= 器は描画したまま disabled + inert（LIVE は opacity .5 で薄くするだけ）
 *   ★ express ウォレットだけは consent でも hidden にする（第三者 iframe で担保が薄いため）。
 *
 * ★ 更新検知は MutationObserver を主・PUB_SUB_EVENTS.cartUpdate 購読を従にする。
 *   <cart-items> と #CartDrawer は innerHTML 差し替えでも要素自体が生存するので、この 2 つを監視すれば
 *   「どの経路が発火したか」を JS が知る必要がなくなる（Dawn の再レンダ経路が増減しても壊れない）。
 *   ★ source === 'product-form' では何もしない: /cart/add.js の応答 sections は追加後にサーバーレンダ
 *     されるので CartDrawer.renderContents（cart-drawer.js:73-89）が入れる HTML は既に判定込みで新鮮。
 *     そこへ自前の差し替えを重ねると setTimeout で張り終えた #CartDrawer-Overlay の click リスナごと
 *     捨ててしまう（オーバーレイで閉じられなくなる・critical H-4）。MutationObserver 方式なら
 *     renderContents の結果を読むだけなので競合しない。
 *
 * ★ 規律（assets/tol-recently-viewed.js:37-39）: custom element は作らない・アロー IIFE ＋
 *   window.__tolXxxLoaded ガード ＋ AbortController teardown ＋ shopify:section:load/unload 対応。
 * ★ pubsub の購読は必ず try/catch（assets/pubsub.js は購読 callback の例外を隔離しない＝1 箇所の
 *   throw が同じ .then 内の renderContents を殺す・assets/tol-pdp-form.js:567-568 の実測記録）。
 *
 * ★ セレクタ写像（Dawn 原本に data 属性を足さない＝5-5 の「checkout ボタン原文温存」規律との両立。
 *   ds04 #8a で文言キー 1 行だけ差し替えたが属性は原文のまま＝本写像は不変・DECISIONS 2026-09-29（6））:
 *   | LIVE の属性                  | 新テーマでの取得元                                                    |
 *   | data-kouza-guard-submit      | [name="checkout"]（main-cart-footer.liquid / cart-notification.liquid） |
 *   |                              |  ＝チェックアウト遷移を起こす機能属性。ドロワーは ds04 #14 で持たない   |
 *   | data-kouza-guard-link        | [data-tol-cart-guard-link]（snippets/tol-cart-login-cta.liquid = tol 所有） |
 *   | data-kouza-guard-overlay     | .cart__dynamic-checkout-buttons / [data-tol-cart-guard-overlay]        |
 *   | #kouza-cart-data ほか         | [data-tol-cart-kouza-*]（snippets/tol-cart-kouza-*.liquid = tol 所有）  |
 */
(() => {
  if (window.__tolCartGuardLoaded) return;
  window.__tolCartGuardLoaded = true;

  const SEL = {
    state: '[data-tol-cart-guard-state]',
    cta: '[data-tol-cart-guard-cta]',
    reason: '[data-tol-cart-guard-reason]',
    fallback: '[data-tol-cart-guard-fallback]',
    realCtas: '[data-tol-cart-guard-real-ctas]',
    wallet: '[data-tol-cart-guard-wallet]',
    checkout: '[name="checkout"]',
    link: '[data-tol-cart-guard-link]',
    overlay: '.cart__dynamic-checkout-buttons, [data-tol-cart-guard-overlay]',
  };

  /* 層 (b)（同意ゲート）の DOM。すべて tol 所有の snippet が出す。 */
  const KZ = {
    data: '[data-tol-cart-kouza-data]',
    item: '.tol-cart-kouza-item',
    agreement: '[data-tol-cart-kouza-agreement]',
    stateBox: '[data-tol-cart-kouza-state]',
    reqs: '[data-tol-cart-kouza-requirements]',
    reqBox: '.tol-cart-kouza__req-checkbox',
    modal: '[data-tol-cart-kouza-modal]',
    panel: '[data-tol-cart-kouza-modal-panel]',
    scroll: '[data-tol-cart-kouza-modal-scroll]',
    read: '[data-tol-cart-kouza-read]',
    back: '[data-tol-cart-kouza-modal-back]',
    close: '[data-tol-cart-kouza-modal-close]',
    termsLink: '[data-tol-cart-kouza-terms-link]',
  };

  /* 層 (b) の状態は **モジュールスコープ**に持つ（DOM に持たせない）＝innerHTML 差し替えで生き残る。
     termsAgreed はページ滞在中のみ有効（リロードで消える＝LIVE 同挙動）。 */
  let termsAgreed = false;
  let lastFingerprint = null;
  let modalOpener = null;
  /* ★ スクロールロックは Dawn のドロワーと同じ .overflow-hidden クラスを共有する（Dawn 慣行）。
     そのため「自分が付けたときだけ外す」フラグを持つ——これが無いと、モーダルを閉じたときに
     ドロワーが付けたロックまで剥がしうる。逆向き（ドロワーの close がモーダルのロックを外す）は
     Dawn 側のコードなので触らない。実際には両方が同時に開く経路は無い
     （モーダルは全画面オーバーレイ＋focus trap なので背後のカートアイコンへ到達できない）が、
     将来 z-index 帯や導線が変わったときに壊れないようにしておく。 */
  let scrollLockedByUs = false;

  /* 合成に使う導出値。satisfied は syncConsentUi() が DOM から導出する。 */
  const consent = { satisfied: false };

  /* ★ Codex #5: /cart 上でドロワー経由でカートを変えると、page 側（primary）の判定ノードだけが stale になる。
   * 機序: CartDrawerItems.updateQuantity は cart-drawer / cart-icon-bubble しか再取得せず（cart-drawer.js:119-134）、
   *   publish する source は CartItems と共有の 'cart-items' 固定（cart.js:218）。page 側 <cart-items> の購読は
   *   source==='cart-items' を自己発火とみなして早期 return する（cart.js:31-35）ので main-cart-footer は更新されない。
   * 対処: 「最後にサーバーから入れ替わったノード」を真実源にする（世代スタンプ）。 */
  let seq = 0;
  const stampNodes = () => {
    document.querySelectorAll(SEL.state).forEach((n) => {
      if (n.__tolSeq !== undefined) return;
      seq += 1;
      /* 初期ロードでは両ノードが同じ新鮮さなので primary に 1 つ分の下駄を履かせて優先させる。
       * ★★ この下駄は **seq 自体にも反映させること**（PR-2 の実 diff レビューで Codex が検出し、
       *   当方が DEV 実機で再現した実バグの是正）。PR-1 は `++seq + 1` と書いており下駄が seq に載らず、
       *   /cart 初期ロードで drawer=1 / page=3 なのに seq は 2 のままだった。その状態で
       *   ドロワー側のノードだけが再生成されると `++seq` = 3 で **page と同値に衝突**し、
       *   下の tie-break（同値なら primary を優先）が **stale な page** を選び続ける。
       *   実測: /cart で講座 2 件 → ドロワー側で 1 件削除 → drawer は blocked=false（新鮮）なのに
       *   page が blocked=true（stale）のまま選ばれ、実 CTA が disabled+hidden に固着した
       *   （リロードでしか復帰しない）。層 (b) と重なると同意を完了しても restriction が真のままで詰む。 */
      if (n.dataset.primary === 'true') seq += 1;
      n.__tolSeq = seq;
    });
  };

  const readState = () => {
    stampNodes();
    const nodes = [...document.querySelectorAll(SEL.state)];
    if (!nodes.length) return null;
    const node = nodes.reduce((best, n) => {
      if (!best) return n;
      if (n.__tolSeq !== best.__tolSeq) return n.__tolSeq > best.__tolSeq ? n : best;
      return best.dataset.primary === 'true' ? best : n;
    }, null);
    if (!node) return null;
    return {
      blocked: node.dataset.blocked === 'true',
      reasonId: node.dataset.reasonId || '',
      reason: node.dataset.reason || '',
      consentReason: node.dataset.consentReason || '',
      hasKouza: node.dataset.hasKouza === 'true',
      hasRestricted: node.dataset.hasRestricted === 'true',
      itemCount: parseInt(node.dataset.itemCount, 10) || 0,
    };
  };

  const setInert = (el, on) => {
    if (on) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  };

  /* ★ 唯一の書き込み口。冪等（同じ入力で何度呼んでも同じ DOM になる）。
   * 一次保証はサーバーレンダ（tol-cart-guard-cta.liquid の呼び元が実 CTA を包む器に付ける）。
   * ここはその器を AJAX 遷移に追随させ、内側の要素にも二重の抑止を掛ける。
   * @param blocked        合成後の抑止（層 (a) or 層 (b)）
   * @param restriction    層 (a) によるブロックか（hidden と代替ボタンはこちらに紐づく）
   * @param consentBlocking 層 (b) だけによるブロックか */
  const applyState = (blocked, reason, state, restriction, consentBlocking) => {
    /* 実 CTA の器（<fieldset>・checkout / 動的決済 / 未ログイン 3 導線をまとめて包んでいる）。
     * ★ disabled が本命（critical B-1）: fieldset[disabled] は子孫 form control を actually disabled にし、
     *   form の default button を disabled 化して implicit submission（数量欄で Enter）を仕様どおり止める。
     * ★ hidden は restriction のときだけ（層 (b) は描画したまま薄くする＝LIVE 逐語）。 */
    document.querySelectorAll(SEL.realCtas).forEach((wrap) => {
      if ('disabled' in wrap) wrap.disabled = blocked;
      wrap.hidden = restriction;
      setInert(wrap, blocked);
    });

    /* express ウォレット（第三者 iframe）は consent でも hidden にする。
       pointer-events はポインタのみ・inert は非対応環境で無効なので、担保が薄い側は隠す。 */
    document.querySelectorAll(SEL.wallet).forEach((el) => {
      el.hidden = blocked;
      setInert(el, blocked);
    });

    /* ★ critical L-1: 空カートでは Dawn 自身が checkout に disabled を出している
     * （main-cart-footer.liquid の {% if cart == empty %}。ドロワーは ds04 #14 で checkout を持たない）。
     * 解除方向で無条件に false を書くとその既定を奪うので、空カートは or に入れて保持する。 */
    const emptyCart = !state || state.itemCount === 0;
    document.querySelectorAll(SEL.checkout).forEach((btn) => {
      btn.disabled = blocked || emptyCart;
      if (blocked) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
      btn.classList.toggle('tol-cart-guard-blocked-btn', blocked);
    });

    /* リンクは disabled 属性が効かないので pointer-events ＋ aria-disabled ＋ tabindex で塞ぐ。
       ★ これらは活性化を止めない層（状態通知とフォーカス順）。実際に止めているのは器の inert と
         下の click ガード。サーバーレンダ側でも同じ属性を出してある（tol-cart-login-cta.liquid）。 */
    document.querySelectorAll(SEL.link).forEach((link) => {
      link.classList.toggle('tol-cart-guard-blocked-btn', blocked);
      if (blocked) {
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      } else {
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      }
    });

    /* 動的チェックアウトとアプリブロックは display:none にせず overlay ＋ inert で塞ぐ。 */
    document.querySelectorAll(SEL.overlay).forEach((el) => {
      el.classList.toggle('tol-cart-guard-blocked', blocked);
      setInert(el, blocked);
    });

    document.querySelectorAll(SEL.cta).forEach((wrap) => {
      const scope = wrap.dataset.scope;
      const reasonEl = wrap.querySelector(SEL.reason);
      /* 代替ボタンはカートページ（scope page）にだけある。ドロワーは ds04 #14 で /cart へのリンク 1 本
         （抑止の対象外）になったので、理由文だけを追随させる。 */
      const fallback = wrap.querySelector(SEL.fallback);

      /* 理由文: 層 (a) は両面。層 (b) は**ドロワーだけ**（/cart は同意ブロックが案内を持つので
         赤い alert を重ねない＝LIVE も層 (b) ではエラー色を使わない）。 */
      let shown = '';
      if (restriction) shown = reason;
      else if (consentBlocking && scope === 'drawer') shown = state ? state.consentReason : '';

      if (reasonEl) {
        /* ★ critical H-1: textContent の代入は「string replace all」で、値が同じでも非空文字列なら
         * 新しい Text ノードが addedNodes に入り childList の mutation record が必ず 1 件積まれる。
         * MutationObserver → scheduleSync → rAF → sync → applyState → … がブロック中ずっと止まらない。
         * 値比較でガードする。 */
        if (reasonEl.textContent !== shown) reasonEl.textContent = shown;
        reasonEl.hidden = !shown;
      }
      /* 代替 disabled ボタンは restriction のときだけ（consent で出すと「押せないチェックアウト」が 2 つ並ぶ）。 */
      if (fallback) fallback.hidden = !restriction;
    });
  };

  /* ================= 層 (b): 規約・資格の同意ゲート ================= */

  /* data-requirements の生値を、本物の配列 / 二重エンコードされた文字列 / 単一文字列 /
     カンマ区切り のいずれであっても配列に正規化する（LIVE :646-671 逐語）。 */
  const parseRequirements = (raw) => {
    if (!raw) return [];
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return [];
    }
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (e2) {
        const trimmed = parsed.replace(/^\[/, '').replace(/\]$/, '');
        parsed = trimmed
          .split(',')
          .map((s) => s.replace(/^"|"$/g, '').replace(/^'|'$/g, '').trim())
          .filter((s) => s.length > 0);
      }
    }
    if (!Array.isArray(parsed)) parsed = parsed ? [String(parsed)] : [];
    return parsed;
  };

  /* カート内 kouza 構成の指紋（line-key:資格件数 を sort して join）。LIVE :673-681 逐語。
     item.key は数量に依存しないので、数量変更ではリセットされない。
     ★ 2026-10: オンライン講座の印（data-online）も含める＝氏名確認チェックの有無が変わったら作り直す。 */
  const buildFingerprint = (dataRoot) => {
    const parts = [];
    dataRoot.querySelectorAll(KZ.item).forEach((b) => {
      const n = parseRequirements(b.getAttribute('data-requirements')).length;
      const online = b.getAttribute('data-online') === 'true' ? ':online' : '';
      parts.push(b.getAttribute('data-line-key') + ':' + n + online);
    });
    return parts.sort().join('|');
  };

  const allRequirementsChecked = () => {
    const boxes = document.querySelectorAll(KZ.reqs + ' ' + KZ.reqBox);
    let ok = true;
    boxes.forEach((b) => {
      if (!b.checked) ok = false;
    });
    return ok;
  };

  /* 資格チェックを隠しデータから生成する。グループ見出しは固定文言（LIVE :762 と同じ設計）。
     ★ LIVE は毎回 innerHTML='' で作り直すが、ここは**指紋が変わったときだけ**再生成する
       （ユーザー裁定 2026-09-03）。新テーマでは同意ブロックの DOM が Dawn の再レンダで壊れないので、
       LIVE と同じ「毎回失う」にするにはわざわざ消しに行く必要がある。指紋が同じなら中身も同じ。
     ★ LIVE :754 の未使用 var title は移植しない（data-product-title 属性は DOM 契約として温存）。 */
  const buildRequirements = (dataRoot, reqList) => {
    const groupLabel = reqList.getAttribute('data-i18n-group') || '';
    reqList.textContent = '';
    dataRoot.querySelectorAll(KZ.item).forEach((block) => {
      const texts = parseRequirements(block.getAttribute('data-requirements'));
      if (texts.length === 0) return;

      const title = document.createElement('div');
      title.className = 'tol-cart-kouza__req-group';
      title.textContent = groupLabel;
      reqList.appendChild(title);

      const key = block.getAttribute('data-line-key');
      texts.forEach((text, idx) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'tol-cart-kouza__req-checkbox';
        input.id = 'TolCartKouzaReq-' + key + '-' + idx;
        const span = document.createElement('span');
        span.textContent = text;
        label.appendChild(input);
        label.appendChild(span);
        reqList.appendChild(label);
      });
    });

    /* ★ 2026-10: タグ「オンライン講座」の商品が 1 件でもあれば、修了証・履修証明書の氏名確認を 1 つだけ足す。
       資格チェックと同じ class（.tol-cart-kouza__req-checkbox）にする＝allRequirementsChecked の対象になり、
       チェックするまでチェックアウトに進めない（同意ゲートにそのまま乗る）。文言は同意欄の data-i18n-online-* から
       （無ければ出さない＝snippet が古いままでも壊れない）。 */
    const hasOnline = [...dataRoot.querySelectorAll(KZ.item)].some(
      (b) => b.getAttribute('data-online') === 'true'
    );
    const onlineText = reqList.getAttribute('data-i18n-online-name') || '';
    if (hasOnline && onlineText) {
      const title = document.createElement('div');
      title.className = 'tol-cart-kouza__req-group';
      title.textContent = reqList.getAttribute('data-i18n-online-group') || '';
      reqList.appendChild(title);

      const label = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'tol-cart-kouza__req-checkbox';
      input.id = 'TolCartKouzaReq-online-name';
      const span = document.createElement('span');
      span.textContent = onlineText;
      label.appendChild(input);
      label.appendChild(span);
      reqList.appendChild(label);
    }
  };

  const syncTermsCheckbox = () => {
    document.querySelectorAll(KZ.stateBox).forEach((box) => {
      if (box.checked !== termsAgreed) box.checked = termsAgreed;
    });
  };

  const isScrolledToBottom = (el) => el.scrollTop + el.clientHeight >= el.scrollHeight - 10;

  const refreshReadGate = () => {
    const modal = document.querySelector(KZ.modal);
    if (!modal) return;
    const scroll = modal.querySelector(KZ.scroll);
    const read = modal.querySelector(KZ.read);
    if (!scroll || !read) return;
    if (isScrolledToBottom(scroll)) read.disabled = false;
  };

  /* ★ opener は**クリックされた要素を明示的に受け取る**（assets/tol-ugc-modal.js の open(card) と同型）。
     document.activeElement に頼ると、ボタンがフォーカスを受けない環境（Safari の既定挙動）や
     プログラム的な click では <body> になり、閉じたときにフォーカスが本文先頭へ飛ぶ（DEV 実機で実発現）。 */
  const openModal = (opener) => {
    const modal = document.querySelector(KZ.modal);
    if (!modal || !modal.hasAttribute('hidden')) return;
    const panel = modal.querySelector(KZ.panel);
    const scroll = modal.querySelector(KZ.scroll);
    const read = modal.querySelector(KZ.read);
    const close = modal.querySelector(KZ.close);

    modalOpener = opener || document.activeElement;
    modal.removeAttribute('hidden');
    if (!document.body.classList.contains('overflow-hidden')) {
      document.body.classList.add('overflow-hidden');
      scrollLockedByUs = true;
    }
    if (scroll) scroll.scrollTop = 0;
    if (read) {
      read.checked = termsAgreed;
      /* 同意済みなら再度触れるようにしておく（LIVE :811-812 逐語）。 */
      read.disabled = !termsAgreed;
    }
    if (typeof trapFocus === 'function' && panel) trapFocus(panel, close || panel);
    else if (close) close.focus();
    /* 本文が短くスクロール不要なケースを拾う（LIVE :813 逐語）。 */
    setTimeout(refreshReadGate, 100);
  };

  /* ★ 冪等。applyState から「同意ブロックごと隠す」ときも必ずここを通す——生の hidden=true を書くと、
     モーダルが開いている最中に AJAX 応答が届いたケースで body.overflow-hidden と trapFocus の
     document リスナーが残り、ページ全体がスクロール不能＋フォーカス閉じ込めのまま復帰しない。
     ★ フォーカスの戻し先が同じフレームで hidden になっているときは null を渡す
       （隠れた要素に focus は当たらず <body> へ飛ぶため）。 */
  const closeModal = () => {
    const modal = document.querySelector(KZ.modal);
    if (!modal || modal.hasAttribute('hidden')) return;
    modal.setAttribute('hidden', '');
    if (scrollLockedByUs) {
      document.body.classList.remove('overflow-hidden');
      scrollLockedByUs = false;
    }
    const target = modalOpener && modalOpener.offsetParent !== null ? modalOpener : null;
    if (typeof removeTrapFocus === 'function') removeTrapFocus(target);
    else if (target) target.focus();
    modalOpener = null;
  };

  /* 層 (b) の UI をカート状態に同期し、consent.satisfied を導出する。
     /cart 以外（同意 UI が DOM に無い面）では即 no-op する。 */
  const syncConsentUi = (state) => {
    const agreement = document.querySelector(KZ.agreement);
    if (!agreement) return; /* /cart 以外＝完全 no-op */

    const dataRoot = document.querySelector(KZ.data);
    /* data root が無い＝未初期化。サーバーが描いた状態をそのまま残す（解除もブロックもしない）。 */
    if (!dataRoot) return;

    if (!state.hasKouza) {
      /* footer は Dawn の再レンダ対象外なので、true→false はここで撤去する。 */
      agreement.hidden = true;
      const reqList = agreement.querySelector(KZ.reqs);
      if (reqList && reqList.childElementCount) reqList.textContent = '';
      closeModal();
      termsAgreed = false;
      lastFingerprint = null;
      syncTermsCheckbox();
      consent.satisfied = false;
      return;
    }

    agreement.hidden = false;

    /* カート内 kouza 構成が変わっていたら同意状態をリセットして資格チェックを作り直す。 */
    const fp = buildFingerprint(dataRoot);
    if (lastFingerprint !== fp) {
      if (lastFingerprint !== null) termsAgreed = false;
      lastFingerprint = fp;
      const reqList = agreement.querySelector(KZ.reqs);
      if (reqList) buildRequirements(dataRoot, reqList);
    }

    syncTermsCheckbox();
    consent.satisfied = termsAgreed && allRequirementsChecked();
  };

  /* ================= 合成と反映 ================= */

  const sync = () => {
    const state = readState();
    if (!state) return; /* fail-safe: サーバーが描いた状態をそのまま残す */

    syncConsentUi(state);

    const restriction = state.blocked;
    const consentBlocking = state.hasKouza && !consent.satisfied;
    const blocked = restriction || consentBlocking;

    applyState(blocked, state.reason, state, restriction, consentBlocking);
  };

  window.tolCartGuard = {
    sync,
    getState: readState,
    /* PR-1 で用意した外部入口。PR-2 で層 (b) が同居したので通常は使わないが API は保つ。
       規約同意フラグ（termsAgreed）を外から立てる/降ろす意味になる。 */
    setConsent(satisfied) {
      termsAgreed = !!satisfied;
      syncTermsCheckbox();
      sync();
    },
  };

  /* ---- 更新検知 ---- */
  let controller = null;
  let rafId = 0;

  const scheduleSync = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      /* ★ critical H-1 の二重防御: sync() 中に自分の書き込みで mutation が積まれても、
       * rafId を先にクリアしないので同フレーム内で新しい rAF を予約されない。 */
      sync();
      rafId = 0;
    });
  };

  const observeRoots = (observer) => {
    ['cart-items', 'cart-drawer-items', '#CartDrawer', '#main-cart-footer'].forEach((sel) => {
      document.querySelectorAll(sel).forEach((root) =>
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-blocked', 'data-reason', 'data-has-kouza', 'data-has-restricted', 'data-item-count'],
        })
      );
    });
  };

  const start = () => {
    if (controller) controller.abort();
    controller = new AbortController();
    const { signal } = controller;

    const observer = new MutationObserver(scheduleSync);
    observeRoots(observer);
    signal.addEventListener('abort', () => observer.disconnect(), { once: true });

    /* ★ 開いたまま破棄されるとスクロールロックと trapFocus の document リスナーが残るので、
       abort で必ず閉じる。shopify:section:load / unload の**両方**が start() を呼び、start() は
       先頭で abort() するので、tol-ugc-modal.js 型の「unload で close() → abort()」順序は
       ここでは成立しない（abort に載せるのが正しい）。 */
    signal.addEventListener('abort', closeModal, { once: true });

    /* ---- 層 (b) の操作（すべて document への委譲＝再レンダで要素が入れ替わっても生き残る） ---- */

    document.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;

        /* 規約モーダルを開く。★ locale 値に埋めた data 属性に依存しない——翻訳文字列は
           「文言の器」であって「DOM 契約」ではない（Translate & Adapt で編集されると消える）。
           同意ブロック内の button を素で拾い、フックが無ければ 1 回だけ警告する。 */
        const inAgreement = t.closest(KZ.agreement);
        const btn = t.closest('button');
        if (inAgreement && btn) {
          if (!btn.matches(KZ.termsLink) && !window.__tolCartKouzaHookWarned) {
            window.__tolCartKouzaHookWarned = true;
            console.warn('[tol-cart-guard] 規約リンクの data フックが見つかりません（翻訳側で失われた可能性）。button で代替します。');
          }
          e.preventDefault();
          openModal(btn);
          return;
        }

        /* モーダルを閉じる（閉じるボタン / 「カートに戻る」 / backdrop）。
           backdrop は e.target === modal のときだけ＝パネル内クリックでは閉じない。 */
        if (t.closest(KZ.close) || t.closest(KZ.back)) {
          e.preventDefault();
          closeModal();
          return;
        }
        if (t.matches(KZ.modal)) {
          closeModal();
          return;
        }

        /* 未ログイン 3 導線の click ガード（LIVE :857-888 を pendingHref 抜きで移植）。
           判定はその時点の状態を readState() から読む（クロージャで固定しない）。
           ★★ これは **inert 非対応環境のフォールバック**であって主たる防御ではない。
             ブロック中は器の <fieldset> に inert が載るので、対応ブラウザでは click 自体が発火せず
             この分岐は走らない（Baseline widely available 2023-04〜）。主たる防御は inert と
             サーバーレンダの disabled で、ここは「古い環境でも押せない」を保証する層。
           ★ 3 導線はカートページにしか無い（ds04 #14 でドロワーから撤去）＝同意ブロックが常に在る。 */
        const link = t.closest(SEL.link);
        if (link) {
          const st = readState();
          if (!st) return;
          const blocked = st.blocked || (st.hasKouza && !consent.satisfied);
          if (!blocked) return;
          e.preventDefault();
          const agreement = document.querySelector(KZ.agreement);
          if (agreement) {
            if (!termsAgreed) openModal(agreement.querySelector('button'));
            else if (agreement.scrollIntoView) agreement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      },
      { signal }
    );

    document.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        /* 読了チェック（モーダル内）と資格チェック（同意ブロック内）。 */
        if (t.matches(KZ.read)) {
          termsAgreed = t.checked;
          syncTermsCheckbox();
          sync();
          return;
        }
        if (t.matches(KZ.reqBox)) sync();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return;
        const modal = document.querySelector(KZ.modal);
        if (modal && !modal.hasAttribute('hidden')) closeModal();
      },
      { signal }
    );

    /* 本文を最下部まで読むまで読了チェックを有効にしない（LIVE :797-805 逐語）。
       モーダルは #main-cart-footer の外＝再レンダ対象外なので要素直付けで生き残る。 */
    const scrollArea = document.querySelector(KZ.scroll);
    if (scrollArea) scrollArea.addEventListener('scroll', refreshReadGate, { signal });

    /* 従: pubsub の保険。MutationObserver が拾えない経路が将来出ても最低限追随する。 */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        try {
          /* product-form 由来は cart-drawer.js の renderContents が新鮮な HTML を入れるので触らない。 */
          if (payload && payload.source === 'product-form') return;
          scheduleSync();
        } catch (e) {
          /* pubsub は例外を隔離しない。ここで throw すると同じ .then 内の renderContents が死ぬ。 */
          console.error('[tol-cart-guard] cartUpdate 購読で例外', e);
        }
      });
      if (typeof unsub === 'function') signal.addEventListener('abort', unsub, { once: true });
    }

    sync();
  };

  start();
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', start);
})();